import * as React from 'react';
import styled from 'styled-components';
import { useController, useFormContext } from 'react-hook-form';
import { Checkbox as KendoCheckbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { FieldProps, generateValidations } from '../../core/form';
import { QuestionOption } from '../../models/QuestionModels';
import { answerSplitCharacter, otherFieldName, otherOptionValue } from '../../core/constants';
import Input from '../appForm/Input';
import { hasValue } from '../../lib/lib';
import { slateGrey, tabletPortraitMinWidth } from '../../appComponents/cssConstants';

const CheckListQuestion = (props: FieldProps) => {
	const { name, data } = props;
	const { setValue } = useFormContext();
	const { field } = useController({ name, rules: generateValidations(props) });
	const { value } = field;

	const handleChange = (e: CheckboxChangeEvent, optionValue: string, answerValue: string, override: boolean) => {
		const checked = e.value;
		let updatedAnswerValue = answerValue;
		if (checked) {
			if (override) updatedAnswerValue = getOverrideOptionValue(data);
			else {
				const answerValueArray = answerValue
					.split(answerSplitCharacter)
					.filter((x) => x !== '' && x !== getOverrideOptionValue(data));
				answerValueArray.push(optionValue);
				updatedAnswerValue = answerValueArray.join(answerSplitCharacter);
			}
		} else {
			const otherFieldValue = getOtherFieldValue(answerValue, data);
			updatedAnswerValue = answerValue
				.split(answerSplitCharacter)
				.filter((x) => x !== optionValue)
				.filter((x) => !(x === otherFieldValue && optionValue === otherOptionValue))
				.join(answerSplitCharacter);
		}
		setValue(name, updatedAnswerValue);
	};

	return (
		<CheckboxContainer>
			{data.map((x: any) => {
				return (
					<React.Fragment key={x.label}>
						<StyledCheckbox
							name={name}
							className={`checkbox ${getCheckboxValue(value, x.value, data) ? 'checked' : ''}`}
							label={x.label}
							value={getCheckboxValue(value, x.value, data)}
							onChange={(e: CheckboxChangeEvent) => handleChange(e, x.value, value, x.override)}
						/>
						{showOtherInput(value, x, data) && (
							<Input
								label="Other (Please specify)"
								name={otherFieldName}
								required
								onBlur={(e) => setValue(otherFieldName, e.target.value)}
							/>
						)}
					</React.Fragment>
				);
			})}
		</CheckboxContainer>
	);
};

export default CheckListQuestion;

const getOtherFieldValue = (answerValue: string, options: QuestionOption<string | number>[]) => {
	const selectedValueData = answerValue?.split(answerSplitCharacter).filter((x) => x !== '');
	// Initial value of empty 'other' input is 'other'(otherOptionValue) from the questionData option,
	// existing saved 'other' input value is the exact string user typed in.
	return selectedValueData?.find((x) => x === otherOptionValue || !options.some((y) => y.value === x));
};

const showOtherInput = (
	answerValue: string,
	currentOption: QuestionOption<string | number>,
	options: QuestionOption<string | number>[]
) => currentOption.showOtherTextField && hasValue(getOtherFieldValue(answerValue, options));

const getCheckboxValue = (
	answerValue: string,
	currentOptionValue: string,
	options: QuestionOption<string | number>[]
) =>
	answerValue?.split(answerSplitCharacter).includes(currentOptionValue) ||
	(currentOptionValue === otherOptionValue && hasValue(getOtherFieldValue(answerValue, options)));

const getOverrideOptionValue = (data: QuestionOption<string | number>[]) =>
	data.find((x) => x.override)?.value as string;

const CheckboxContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: stretch;

	> div {
		display: inline-block;
		margin-bottom: 8px;
		width: 100%;

		@media (min-width: ${tabletPortraitMinWidth}) {
			width: calc(50% - 10px);

			&:nth-child(odd) {
				margin-right: 20px;
			}
		}
	}

	// TODO: Remove dependency with Mui
	.MuiTextField-root {
		margin-bottom: 0;
	}
`;

const StyledCheckbox = styled(KendoCheckbox)`
	display: none;

	& + .k-checkbox-label {
		width: 100%;
		height: 100%;
		padding: 16px 24px;
		margin-left: 0;
		font-size: 16px;
		line-height: 19px;
		color: ${slateGrey};
		background: #fff;
		border: 1px solid #e5e5e5;
		border-radius: 26px;
		display: flex;
		align-items: center;
	}

	&.checked + .k-checkbox-label {
		color: #fff;
		background: ${(props) => props.theme.brandingColourHeavy};
		border-color: ${(props) => props.theme.brandingColourHeavy};
	}
`;
